<template>
  <z-input
    ref="input"
    v-model="computedValue"
    class="zoox-component zoox-phone-input"
    autocomplete="off"
    outlined
    type="tel"
    inputmode="tel"
    persistent-hint
    :readonly="readonly"
    :maxlength="currentMask.length"
    :disabled="disabled"
    :class="{small}"
    :large="large"
    :small="small"
    :medium="medium"
    :hint="hint"
    :label="label"
    :dense="isDense"
    :hide-details="hideDetails"
    :clearable="clearable"
    :error-messages="errorMessages"
    v-bind="$attrs"
    @keypress="isNumber"
    @focus="openSelect"
  >
    <template #prepend-inner>
      <z-descrete-select
        ref="selectDdi"
        :menu-props="menuProps"
        :disabled="disabled || readonly"
        :x-small="small"
        :items="filteredCountries"
        :value="currentCountry"
        :item-width="300"
        @change="onChangeCountry"
      >
        <template #selected>
          <z-country-flag
            v-if="selectedCountry.code"
            class="country-flag"
            rounded
            :country="selectedCountry.code"
            :size="small ? 16 : 24"
          />
          <span v-else>DDI</span>
        </template>

        <template #option="{option}">
          <z-country-flag rounded :country="option.code" :size="24" />
          <span class="mx-4">{{ option.name }} </span>
          <span class="ddi">{{ option.dial }}</span>
        </template>
      </z-descrete-select>
    </template>
  </z-input>
</template>

<script>
import get from 'lodash.get'
import inputControlPropsMixin from '../inputControlPropsMixin'

/**
 * @displayName Input Phone
 */
export default {
  name: 'ZPhoneInput',
  mixins: [inputControlPropsMixin],
  props: {
    /**
     * País atual selecionado (usado para aplicar a máscara)
     */
    currentCountry: {
      type: String,
      required: true,
    },
    /**
     * Adiciona funcionalidade de "limpar" o input
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Países possíveis utilizando a estrutura (ver exemplo)
     */
    countries: {
      type: Array,
      default: () => [],
    },
    /**
     * Valor do telefone
     */
    value: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Flag para utilizar a mascara mobile
     */
    isMobile: {
      type: Boolean,
      default: false,
    },

    menuProps: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    computedValue: {
      get: function () {
        return this.formatValue(this.value, this.currentMask)
      },

      set: function (newValue) {
        this.$emit('input', this.formatValue(newValue, this.currentMask))

        if (!newValue.includes(this.selectedCountry.dial)) {
          this.$nextTick(() => {
            this.openSelect()
          })

          this.$emit('change:country', '')
        }

        if (!this.currentCountry) {
          const ddiOfCountry = this.countries.filter((country) => {
            return country.dial === newValue
          })

          if (ddiOfCountry.length === 1) {
            this.onChangeCountry(ddiOfCountry[0].code)
            this.closeSelect()
          }
        }
      },
    },

    currentMask() {
      if (Array.isArray(this.selectedCountry.mask)) {
        return this.isMobile
          ? get(this.selectedCountry, 'mobileMask[0]') ||
              get(this.selectedCountry, 'mask[0]')
          : get(this.selectedCountry, 'mask[0]')
      }

      return '+###'
    },

    selectedCountry() {
      return (
        this.countries.find(
          (country) => country.code === this.currentCountry
        ) || {}
      )
    },

    filteredCountries() {
      if (this.value && !this.currentCountry) {
        return this.countries
          .filter((country) => country.dial !== null)
          .filter((country) => country.dial.includes(this.value))
          .map((country) => ({...country, value: country.code}))
      }

      return this.countries.map((country) => ({
        ...country,
        value: country.code,
      }))
    },
  },

  watch: {
    /* 
      necessário para emitir o valor com a máscara correta
      quando o model é alterado depois da inicialização do componente
      sem a interação do usuário
    **/
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.$emit('input', this.formatValue(newValue, this.currentMask))
        }
      },
    },
  },

  methods: {
    formatValue(value, mask) {
      let cleanValue = this.sanitizeValue(value)
      let result = ''
      let count = 0

      if (value) {
        let arrayValue = cleanValue.toString().split('')
        let arrayMask = mask.toString().split('')

        for (let i = 0; i < arrayMask.length; i++) {
          if (i < arrayValue.length + count) {
            if (arrayMask[i] === '#') {
              result = result + arrayValue[i - count]
            } else {
              result = result + arrayMask[i]
              count++
            }
          }
        }
      }

      return result
    },

    sanitizeValue(value) {
      let result = ''

      if (value) {
        let arrayValue = value.toString().split('')

        for (let i = 0; i < arrayValue.length; i++) {
          if (this.isValid(arrayValue[i])) {
            result = result + arrayValue[i]
          }
        }
      }

      return result
    },

    isNumber(evt) {
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      const keyPressed = evt.key

      if (!keysAllowed.includes(keyPressed)) {
        evt.preventDefault()
      }
    },

    isValid(value) {
      let letterNumber = /^[0-9a-zA-Z]+$/

      return value.match(letterNumber) ? true : false
    },

    openSelect() {
      if (!this.currentCountry) {
        setTimeout(() => {
          this.$refs.selectDdi.open()
        }, 200)
      }
    },
    closeSelect() {
      if (!this.currentCountry) {
        setTimeout(() => {
          this.$refs.selectDdi.close()
        }, 200)
      }
    },
    onChangeCountry(countryValue) {
      /**
       * Evento emitido ao mudar de país pelo select
       *
       * @property {String} countryValue - Valor do país selecionado
       */
      const country = this.countries.find(
        (country) => country.code === countryValue
      )

      this.$emit('change:country', countryValue)
      this.$emit('input', country.dial)
    },
  },
}
</script>

<style lang="scss">
@use '@scss/global' as g;

.zoox-component {
  &.zoox-phone-input {
    &.small {
      input {
        padding-top: 12px;
      }
    }

    .ddi {
      color: rgba(0, 0, 0, 0.6);
    }

    .country-flag {
      height: auto;
      width: 24px;
    }

    .zoox-descrete-select {
      padding: 2px !important;

      i {
        display: none;
      }
    }
  }
}

.zoox-phone-input:not(.error--text) {
  .v-input__control {
    .v-input__slot:hover {
      fieldset {
        color: var(--primary) !important;
      }
    }
  }
}
</style>
